/**
 * Core Hangman Game Types
 *
 * All TypeScript types and interfaces for the Hangman word guessing game.
 * Single-player: guess letters to reveal a hidden word before the figure is complete.
 */

// ============================================================================
// Difficulty & Category
// ============================================================================

export type HangmanDifficulty = 'easy' | 'medium' | 'hard';

export type HangmanCategory =
  | 'all'
  | 'animals'
  | 'countries'
  | 'foods'
  | 'sports'
  | 'technology'
  | 'movies';

// ============================================================================
// Constants
// ============================================================================

/** Maximum wrong guesses before the game is lost */
export const MAX_WRONG_GUESSES = 6;

/** All 26 uppercase letters */
export const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// ============================================================================
// Game State
// ============================================================================

export interface HangmanGameState {
  /** The secret word (uppercase) */
  word: string;
  /** The category the word belongs to */
  category: HangmanCategory;
  /** Difficulty level */
  difficulty: HangmanDifficulty;
  /** Set of guessed letters (uppercase) */
  guessedLetters: string[];
  /** Letters that were correct guesses */
  correctLetters: string[];
  /** Letters that were wrong guesses */
  wrongLetters: string[];
  /** Number of wrong guesses so far */
  wrongGuessCount: number;
  /** Current game status */
  gameStatus: 'idle' | 'playing' | 'won' | 'lost';
  /** Whether the hint has been used this game */
  hintUsed: boolean;
  /** Unique game identifier */
  gameId: string;
  /** Timestamp when the game started */
  startedAt: number;
  /** Elapsed time in seconds */
  timer: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface HangmanGameSettings {
  difficulty: HangmanDifficulty;
  category: HangmanCategory;
}

// ============================================================================
// Statistics
// ============================================================================

export interface HangmanDifficultyStats {
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  totalGuesses: number;
  totalWrongGuesses: number;
  hintsUsed: number;
  winPercentage: number;
  currentStreak: number;
  bestStreak: number;
}

export interface HangmanStatistics {
  userId?: string;
  easy: HangmanDifficultyStats;
  medium: HangmanDifficultyStats;
  hard: HangmanDifficultyStats;
  totalGames: number;
  totalPlayTime: number;
  currentStreak: number;
  bestStreak: number;
}

export interface HangmanGameResult {
  gameId: string;
  difficulty: HangmanDifficulty;
  category: HangmanCategory;
  won: boolean;
  word: string;
  guessCount: number;
  wrongGuessCount: number;
  hintUsed: boolean;
  time: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface HangmanGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface HangmanGameModeSelectorProps {
  onStartGame: (settings: HangmanGameSettings) => void;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

export interface HangmanBoardProps {
  gameState: HangmanGameState;
  onGuessLetter: (letter: string) => void;
  disabled?: boolean;
}

// ============================================================================
// Word Lists by Category
// ============================================================================

export const WORD_LISTS: Record<Exclude<HangmanCategory, 'all'>, string[]> = {
  animals: [
    'ELEPHANT', 'GIRAFFE', 'PENGUIN', 'DOLPHIN', 'BUTTERFLY',
    'KANGAROO', 'CHEETAH', 'OCTOPUS', 'FLAMINGO', 'GORILLA',
    'PANTHER', 'HAMSTER', 'PARROT', 'TURTLE', 'RABBIT',
    'FALCON', 'JAGUAR', 'SALMON', 'LIZARD', 'MONKEY',
    'TIGER', 'HORSE', 'EAGLE', 'SHARK', 'WHALE',
  ],
  countries: [
    'AUSTRALIA', 'BRAZIL', 'CANADA', 'GERMANY', 'JAPAN',
    'FRANCE', 'MEXICO', 'SWEDEN', 'PORTUGAL', 'ARGENTINA',
    'THAILAND', 'COLOMBIA', 'INDONESIA', 'SWITZERLAND', 'SINGAPORE',
    'NORWAY', 'TURKEY', 'EGYPT', 'INDIA', 'SPAIN',
    'ITALY', 'CHINA', 'CHILE', 'KENYA', 'NEPAL',
  ],
  foods: [
    'CHOCOLATE', 'SPAGHETTI', 'HAMBURGER', 'PINEAPPLE', 'AVOCADO',
    'PANCAKES', 'BROCCOLI', 'SANDWICH', 'MUSHROOM', 'CINNAMON',
    'BLUEBERRY', 'CROISSANT', 'DUMPLING', 'NOODLES', 'POPCORN',
    'PRETZEL', 'BURRITO', 'LASAGNA', 'WAFFLE', 'MUFFIN',
    'PIZZA', 'SUSHI', 'TACO', 'BREAD', 'STEAK',
  ],
  sports: [
    'BASKETBALL', 'SWIMMING', 'VOLLEYBALL', 'GYMNASTICS', 'BADMINTON',
    'WRESTLING', 'SNOWBOARD', 'BASEBALL', 'FOOTBALL', 'LACROSSE',
    'ARCHERY', 'CRICKET', 'CYCLING', 'FENCING', 'SURFING',
    'BOWLING', 'SKATING', 'RUNNING', 'BOXING', 'TENNIS',
    'RUGBY', 'GOLF', 'POLO', 'JUDO', 'KAYAK',
  ],
  technology: [
    'COMPUTER', 'INTERNET', 'SOFTWARE', 'ALGORITHM', 'DATABASE',
    'BLUETOOTH', 'KEYBOARD', 'ROBOTICS', 'FIREWALL', 'ETHERNET',
    'PROCESSOR', 'SATELLITE', 'STREAMING', 'FRAMEWORK', 'COMPILER',
    'TERMINAL', 'BROWSER', 'NETWORK', 'DIGITAL', 'SILICON',
    'PIXEL', 'CLOUD', 'VIRUS', 'DRONE', 'LASER',
  ],
  movies: [
    'INCEPTION', 'GLADIATOR', 'TITANIC', 'AVATAR', 'INTERSTELLAR',
    'BRAVEHEART', 'SPOTLIGHT', 'PARASITE', 'WHIPLASH', 'MOONLIGHT',
    'FROZEN', 'GRAVITY', 'ARRIVAL', 'DUNKIRK', 'JOKER',
    'ROCKY', 'ALIEN', 'JAWS', 'SHREK', 'BRAVE',
    'COCO', 'DUNE', 'CARS', 'BOLT', 'TENET',
  ],
};

/** Difficulty word-length ranges */
export const DIFFICULTY_WORD_LENGTH: Record<HangmanDifficulty, { min: number; max: number }> = {
  easy: { min: 4, max: 5 },
  medium: { min: 6, max: 8 },
  hard: { min: 9, max: Infinity },
};
